import { put, fork, spawn, select, call, takeLatest } from "redux-saga/effects";
import ACTION from "../action";
import { ApiNames } from "../../api/api";
import { actions, getRecentPatientsAction } from "./sagaActions";
import { watchSignInActions } from "./authActions";

import { callApi } from "./networkActions";
import { selectNetworkActionsError, selectIDPConfig, selectCurrentPage, selectAPSponsors } from "../selector";
import { getAuthInstance, logout } from "@tailormed/common-client/services/auth";
import { waitForLogin } from "@tailormed/common-client/store/saga/authSagas";
import initializeUserMetrics from "@tailormed/common-client/store/saga/services/userMetrics";
import { oktaConfig } from "../../constant/config";
import { getSkipLimit } from "../../constant/general";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { USER_ROLES } from "../../constant/user";

function* getAssignees() {
  const assigneesRes = yield callApi(ApiNames.Assignees);
  const assignees = assigneesRes.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
  yield put(ACTION.setAssignees(assignees));
}

function* getUsers() {
  const userRes = yield callApi(ApiNames.Users);
  const users = userRes.users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
  yield put(ACTION.setUsers(users));
}

export function* getPatients(
  showHistoricOrders = "false",
  orderDateFilter,
  adminDateFilter,
  estimatedDeliveryDateFilter,
  receivedDateFilter,
  billVoidedDateFilter,
  assigneesFilter,
  facilitiesFilter,
  manufacturersFilter,
  physiciansFilter,
  statusesFilter,
  orderTypesFilter,
  billVoidedFilter,
  drugsFilter,
  drugDoseFormCategoriesFilter,
  orderViewFilter,
  isUnassigned,
  page,
  apiName = ApiNames.Search,
  sorting,
  noPagination
) {
  const { skip, limit } = getSkipLimit(page);
  const searchRes = yield callApi(
    apiName,
    showHistoricOrders,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned,
    skip,
    limit,
    sorting,
    noPagination
  );

  if (page > 1 && searchRes.patients.length < 1) {
    const currentPage = yield select(selectCurrentPage);
    yield put(ACTION.setCurrentPage(currentPage - 1));
    yield put(ACTION.setIsLoadingEnabled(true));
    yield put(
      ACTION.setFiltersAction(
        {
          showInactiveFilter: showHistoricOrders,
          orderDateFilter,
          adminDateFilter,
          estimatedDeliveryDateFilter,
          receivedDateFilter,
          billVoidedDateFilter,
          assigneesFilter,
          facilitiesFilter,
          manufacturersFilter,
          physiciansFilter,
          statusesFilter,
          orderTypesFilter,
          billVoidedFilter,
          drugsFilter,
          drugDoseFormCategoriesFilter,
          orderViewFilter,
          isUnassigned,
          forceCallApi: true
        },
        ApiNames.SearchWithoutRefresh,
        currentPage - 1,
        sorting
      )
    );
  } else {
    yield put(ACTION.setPatients(searchRes.patients));
    yield put(ACTION.setTotalPatients(searchRes.total));
  }
}

function* getAPSponsors() {
  const result = yield callApi(ApiNames.getAPSponsors);
  if (result) {
    yield put(ACTION.APSponsors(result.APSponsors));
  } else {
    ErrorMessage(`Failed to get APSponsors data`);
  }
}

function* getUserGroups() {
  const result = yield callApi(ApiNames.UserGroups);
  if (result) {
    const userGroups = result["user-groups"].reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setUserGroups(userGroups));
  }
}

function* getUserGroupsWithUsers() {
  try {
    const result = yield callApi(ApiNames.UserGroupsWithUsers);

    if (result) {
      yield put(ACTION.setUserGroupsWithUsers(result));
    } else {
      ErrorMessage(`Failed to get user groups with users`);
    }
  } catch (error) {
    ErrorMessage(`Error fetching user groups with users: ${error.message}`);
  }
}

function* getUnits() {
  const getUnitsRes = yield callApi(ApiNames.GetUnits);
  yield put(ACTION.setUnites(getUnitsRes));
}

function* getUser() {
  const loggedUserRes = yield callApi(ApiNames.LoggedUser);
  if (loggedUserRes) {
    const user = loggedUserRes.data.users[0];
    const isPhiAllowed = loggedUserRes.data.phiAllowed;
    yield put(ACTION.setEnvironment(loggedUserRes.data.siteLicense.siteName));
    yield put(ACTION.setUser({ ...user, isPhiAllowed }));

    // Allow Pharmacy-only users to manage their preferences for program open email notifications
    if (user.role === USER_ROLES.PHARMACOLOGIST) {
      const notificationPreferencesRes = yield callApi(ApiNames.NotificationPreferences);
      if (notificationPreferencesRes) {
        yield put(ACTION.setNotificationPreferences(notificationPreferencesRes));
      } else {
        ErrorMessage("Failed to load notification preferences");
      }
    }
  } else {
    const [loggedUserError] = yield select(selectNetworkActionsError([ApiNames.LoggedUser]));
    const idpConfig = yield select(selectIDPConfig);
    if (loggedUserError.msg === "Request failed with status code 401") {
      ErrorMessage("User unauthorized to access Pharmacy");
    }
    yield call(logout, null, idpConfig, oktaConfig.loginURI);
  }
}

function* getSettings() {
  const settingsRes = yield callApi(ApiNames.Settings);
  yield put(ACTION.setSettings(settingsRes.settings));
}

function* fetchAPSponsorsSaga() {
  const apSponsors = yield select(selectAPSponsors);
  if (apSponsors && Array.isArray(apSponsors) && apSponsors.length > 0) {
    return;
  }
  yield call(getAPSponsors);
}

function* watchFetchAPSponsors() {
  yield takeLatest(ACTION.Types.FETCH_AP_SPONSORS_ACTION, fetchAPSponsorsSaga);
}

function* startup() {
  const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
  const token = oktaAuth.getAccessToken();
  yield spawn(getSettings);
  yield spawn(getAssignees);
  yield call(getUser);
  yield spawn(initializeUserMetrics, { analytics: true, rum: true, token });
  yield spawn(getUserGroups);
  yield spawn(getUserGroupsWithUsers);
  yield spawn(getUsers);
  yield spawn(getUnits);
  yield spawn(getRecentPatientsAction, { selectedPage: 1 });
}

function* rootSaga() {
  yield fork(watchSignInActions);
  yield waitForLogin();
  yield fork(startup);
  yield spawn(actions);
  yield spawn(watchFetchAPSponsors);
}

export { rootSaga };
